import $ = require('jquery');
import DragSource = require('./DragSource');
import DropZone = require('./DropZone');
/**
 * Generic drag & drop manager class
 */
    class DragDrop {
        /**
         * drag sources registered with the manager
         */
        sources: DragSource[] = null;
        /**
         * drop zones registered with the manage
         */
        zones: DropZone[] = null;
        /**
         * drop zone being hovered during a drag
         */
        currentDropZone: DropZone = null;
        /**
         * drag source being dragged
         */
        currentDragSource: DragSource = null;
        /**
         * whether a drop was made on a drop zone.
         * We need this flag so we can detect when we ended the
         * drag without dropping on a dropzone
         */
        wasDropped: boolean = null;

        /**
         * Keep a stack of DropZones being hovered at a given time, sorted
         * by calculated zIndex (descending)
         */
        hoverStack: DropZone[] = null;

        constructor() {
            this.sources = [];
            this.zones = [];
            this.hoverStack = [];
        }

        /**
         * Adds a drag source to the manager
         */
        addDragSource(dragSource: DragSource) {
            if (dragSource instanceof DragSource) {
                dragSource.init();

                dragSource.element
                    .bind('ddstart.dragdrop', $.proxy(this.onDragStart, this))
                    .bind('dddrag.dragdrop', $.proxy(this.onDrag, this))
                    .bind('ddstop.dragdrop', $.proxy(this.onDragStop, this));

                this.sources.push(dragSource);
            }
        }

        /**
         * Removes all or some of the registered drag sources from the manager,
         * based on the parameters passed to the method
         *
         * @param filterClb a callback that is executed for each drop zone and returns
         *                  whether the drop zone will be removed or not
         */
        removeDragSources(filterClb?: (source: DragSource) => boolean) {
            let newSources: DragSource[] = [];
            for (let i = 0; i < this.sources.length; ++i) {
                if (!filterClb || filterClb(this.sources[i])) {
                    this.sources[i].element.unbind('.dragdrop');
                    this.sources[i].destroy();
                    this.sources[i] = null;
                } else {
                    newSources.push(this.sources[i]);
                }
            }
            this.sources = newSources;
        }

        /**
         * Adds a drop zone to the manager
         */
        addDropZone(dropZone: DropZone) {
            if (dropZone instanceof DropZone) {
                dropZone.element
                    .bind('ddover.dragdrop', $.proxy(this.onDropZoneOver, this))
                    .bind('ddout.dragdrop', $.proxy(this.onDropZoneOut, this))
                    .bind('dddrop.dragdrop', $.proxy(this.onDropZoneDrop, this));

                this.zones.push(dropZone);
            }
        }

        /**
         * Removes all or some of the registered drop zones, based on the parameters
         * passed to the method
         *
         * @param filterClb a callback that is executed for each drop zone and returns
         *                  whether the drop zone will be removed or not
         */
        removeDropZones(filterClb?: (zone: DropZone) => boolean) {
            let newZones: DropZone[] = [];
            for (let i = 0; i < this.zones.length; ++i) {
                if (!filterClb || filterClb(this.zones[i])) {
                    this.zones[i].element.unbind('.dragdrop');
                    this.zones[i].destroy();
                    if (this.zones[i] === this.currentDropZone) {
                        this.currentDropZone = null;
                    }
                } else {
                    newZones.push(this.zones[i]);
                }
            }
            this.zones = newZones;
        }

        activateDropZones() {
            for (let i = 0; i < this.zones.length; ++i) {
                this.zones[i].activate();
            }
        }

        deactivateDropZones() {
            for (let i = 0; i < this.zones.length; ++i) {
                this.zones[i].deactivate();
            }
        }

        onDropZoneChanged(newDropZone: DropZone, oldDropZone: DropZone) {
            // abstract
        }

        _handleCurrentZoneChange() {
            let stackTop = this.hoverStack.length ? this.hoverStack[0] : null;
            if (this.currentDropZone !== stackTop) {
                if (this.currentDropZone) {
                    this.currentDropZone.updateView(null);
                }

                let oldDropZone = this.currentDropZone;
                this.currentDropZone = stackTop;
                this.currentDragSource.updateView(this.currentDropZone);

                if (this.currentDropZone) {
                    this.currentDropZone.updateView(this.currentDragSource);
                }

                this.onDropZoneChanged(this.currentDropZone, oldDropZone);
            }
        }

        /**
         * Triggers when the mouse enters a drop zone
         *
         * @param e original jquery event
         * @param ui the jquery ui hash passed from the droppable/sortable
         * @param dropZone the drop zone being hovered
         */
        onDropZoneOver(e: JQueryEventObject, ui: {}, dropZone: DropZone) {
            // add dropZone to hoverstack if it's not already added
            if ($.inArray(dropZone, this.hoverStack) === -1) {
                // add element to hoverStack
                this.hoverStack.push(dropZone);
            }
            this.hoverStack.sort(function(a, b) {
                return b.getZIndex() - a.getZIndex();
            });
            this._handleCurrentZoneChange();
        }

        /**
         * Triggers when the mouse leaves a drop zone
         *
         * @param e original jquery event
         * @param ui the jquery ui hash passed from the droppable/sortable
         * @param dropZone the drop zone that was left
         */
        onDropZoneOut(e: JQueryEventObject, ui: {}, dropZone: DropZone) {
            // remove element from hoverStack
            for (let i = 0; i < this.hoverStack.length; i++) {
                if (this.hoverStack[i] === dropZone) {
                    this.hoverStack.splice(i, 1);
                    break;
                }
            }
            this._handleCurrentZoneChange();
        }

        /**
         * Triggered when the source is dropped on a drop zone
         *
         * @param e original jquery event
         * @param ui the jquery ui hash passed from the droppable/sortable
         * @param dropZone the drop zone that received the drop
         */
        onDropZoneDrop(e: JQueryEventObject, ui: {}, dropZone: DropZone) {
            // accept only drops only on drop zones on the top
            // of the hover stack, i.e. the current one
            if (this.currentDropZone === dropZone) {
                this.currentDragSource.drop(dropZone);
                this.wasDropped = true;
            }
        }

        /**
         * Triggered when dragging starts
         *
         * @param e original jquery event
         * @param ui the jquery ui hash passed from the draggable
         * @param dragSource the drag source object representing what's being dragged
         */
        onDragStart(e: JQueryEventObject, ui: {}, dragSource: DragSource) {
            this.hoverStack = [];
            this.currentDropZone = null;
            this.currentDragSource = dragSource;
            this.activateDropZones();

            dragSource.updateView(null);

            this.wasDropped = false;
        }

        /**
         * Triggered during drag, on mouse move
         *
         * @param e original jquery event
         * @param ui the jquery ui hash passed from the draggable
         * @param dragSource the drag source object representing what's being dragged
         */
        onDrag(e: JQueryEventObject, ui: {}, dragSource: DragSource) {
            // abstract
        }

        /**
         * Triggered when dragging ends
         *
         * @param e original jquery event
         * @param ui the jquery ui hash passed from the draggable
         * @param dragSource the drag source object representing what's being dragged
         */
        onDragStop(e: JQueryEventObject, ui: {}, dragSource: DragSource) {
            if (!this.wasDropped) {
                // We dropped where there isn't any drop zone
                this.currentDragSource.drop(null);
            }

            this.deactivateDropZones();
            this.currentDragSource = null;
        }

        destroy() {
            this.removeDragSources();
            this.removeDropZones();
        }
    }

export = DragDrop;



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/common/js/dd/DragDrop.ts
 **/